import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * @fileoverview SMS Notification Toggle Tracking
 *
 * This is the new standardized way to track SMS notification interactions across the application.
 * This replaces previous ad-hoc tracking implementations and provides a consistent interface
 * for tracking SMS notification enable/disable events from various contexts.
 *
 * @example
 * // In a component:
 * const trackSmsToggle = useTrackSmsNotificationToggle();
 *
 * trackSmsToggle({
 *   actionTaken: TrackSmsNotificationToggleActionTaken.Enabled,
 *   eventTrigger: TrackSmsNotificationToggleEventTrigger.SavePrompt
 * });
 *
 * @example
 * // Direct usage (e.g., in Redux actions):
 * trackSmsNotificationToggle({
 *   actionTaken: TrackSmsNotificationToggleActionTaken.Disabled,
 *   eventTrigger: TrackSmsNotificationToggleEventTrigger.EmailsAndNotifications,
 *   description: 'User interacts with the SMS notifications',
 *   pageName: 'Notifications',
 *   url: '/notifications'
 * });
 */

const TRACK_SMS_NOTIFICATION_TOGGLE_DESCRIPTION = 'User interacts with the SMS notifications';

export enum TrackSmsNotificationToggleEventTrigger {
    AuctionRegistration = 'Auction Registration',
    EmailsAndNotifications = 'Emails & Notifications',
    LeadingBidder = 'Leading Bidder',
    SavePrompt = 'Save Prompt',
}

type TrackSmsNotificationTogglePayload = TrackingPayload & {
    auctionRemindersEnabled: boolean;
    description: string;
    eventTrigger: TrackSmsNotificationToggleEventTrigger;
    outbidEnabled: boolean;
    paymentRemindersEnabled: boolean;
};

export const trackSmsNotificationToggle = (payload: TrackingPayload) => {
    track(TrackingEvents.Notifications.SMSNotificationToggle, payload);
};

export const useTrackSmsNotificationToggle = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return useCallback(
        ({
            auctionRemindersEnabled,
            eventTrigger,
            outbidEnabled,
            paymentRemindersEnabled,
        }: Omit<TrackSmsNotificationTogglePayload, 'pageName' | 'url' | 'description'>) => {
            trackSmsNotificationToggle({
                auctionRemindersEnabled,
                description: TRACK_SMS_NOTIFICATION_TOGGLE_DESCRIPTION,
                eventTrigger,
                outbidEnabled,
                pageName: getAmplitudePageNameFromPath(pathname),
                paymentRemindersEnabled,
                url,
            });
        },
        [pathname, url]
    );
};
